import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft, MapPin, Building2, ChevronRight,
    ChevronLeft, ChevronRight as ChevronRightIcon,
    Car, Package, FlameKindling, Droplets, Zap,
    CheckCircle2, XCircle, Phone, Mail, Calendar,
    Layers, Users, Clock, DollarSign, Send,
    FileText, Shield, Utensils, Star, Lock, Heart
} from 'lucide-react';
import { useRentalProperty } from '@/hooks/useRental';
import { useOfferRental, useMyRentalOffers } from '@/hooks/useOffer';
import { useMySignedNdas } from '@/hooks/useNda';
import { useToggleSaveListing } from '@/hooks/useSavedListings';
import { useAuthStore } from '@/store/authStore';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/numberInput';
import { NdaSignModal, OfferModal, MediaGallery } from '@/components/common';
import { Textarea } from '@/components/ui/misc';
import { formatNumber } from '@/utils';

/* ─── helpers ─── */
const fmtMoney = (v: string | null | undefined, currency = 'VND') => {
    if (!v) return '—';
    const n = parseFloat(v);
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} tỷ ${currency}`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)} tr ${currency}`;
    return `${formatNumber(n)} ${currency}`;
};
const getPrimaryImg = (images: any[]) =>
    (images?.find(i => i.isPrimary) ?? images?.[0])?.image?.publicUrl ?? null;



/* ─── Stat box ─── */
function StatBox({ icon, label, value, accent }: {
    icon: React.ReactNode; label: string; value: string; accent?: boolean;
}) {
    return (
        <div className="rounded-xl p-3 border" style={{
            background: accent ? 'var(--brand-pale)' : 'var(--surface-1)',
            borderColor: accent ? 'rgba(249,115,22,0.2)' : 'var(--border-c)',
        }}>
            <div className="flex items-center gap-1.5 mb-1" style={{ color: accent ? 'var(--brand)' : 'var(--text-3)' }}>
                {icon}
                <span className="text-[11px] font-medium">{label}</span>
            </div>
            <p className="font-bold text-sm leading-tight" style={{ color: accent ? 'var(--brand)' : 'var(--text-1)' }}>
                {value}
            </p>
        </div>
    );
}

/* ─── Bool feature row ─── */
function FeatureRow({ icon, label, value, detail }: {
    icon: React.ReactNode; label: string; value: boolean; detail?: string | null;
}) {
    return (
        <div className="flex items-center justify-between gap-2 py-2.5 border-b last:border-0"
            style={{ borderColor: 'var(--border-c)' }}>
            <div className="flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
                <span style={{ color: 'var(--text-3)' }}>{icon}</span>
                <span className="text-sm">{label}</span>
                {detail && <span className="text-xs px-2 py-0.5 rounded-full fnb-tag">{detail}</span>}
            </div>
            {value
                ? <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                : <XCircle size={16} style={{ color: 'var(--text-3)' }} className="shrink-0 opacity-40" />
            }
        </div>
    );
}

/* ─── Apply offer schema ─── */
const offerSchema = z.object({
    message: z.string().min(10, 'Tối thiểu 10 ký tự'),
    proposedRent: z.preprocess(
        v => (v === '' || v === undefined ? undefined : Number(String(v).replace(/\D/g, ''))),
        z.number().positive('Nhập mức giá đề xuất').optional(),
    ),
});
type OfferForm = z.infer<typeof offerSchema>;

/* ─── Main Page ─── */
export default function RentalDetailPage() {
    const { id } = useParams<{ id: string }>();
    const { user, isAuthenticated } = useAuthStore();
    const [offerOpen, setOfferOpen] = useState(false);
    const [sent, setSent] = useState(false);
    const [ndaOpen, setNdaOpen] = useState(false);

    const { data, isLoading, error, refetch } = useRentalProperty(id!);
    const property = (data as any)?.data ?? data;

    const { data: myOffers } = useMyRentalOffers(user?.id);
    const isOffered = Array.isArray(myOffers) ? myOffers.some((o: any) => o.entityId === property?.id) : false;

    const isNdaError = error && (error as any).response?.data?.message === "Please Sign NDA";

    const { data: mySignedNdas } = useMySignedNdas();
    const isSignedNda = Array.isArray(mySignedNdas) ? mySignedNdas.some((n: any) => n.entityId === property?.id && n.entityType === 'Rental') : false;
    const ndaRequired = property?.ndaRequired === true;
    const needsToSignNda = ndaRequired && !isSignedNda;

    const { isSaved, toggleSave } = useToggleSaveListing();
    const saved = property ? isSaved(property.id) : false;

    const { mutate: createOffer, isPending } = useOfferRental();

    const {
        register, handleSubmit, reset,
        formState: { errors },
    } = useForm<OfferForm>({ resolver: zodResolver(offerSchema) });

    const onSubmit = (form: OfferForm) => {
        if (!property || !user) return;
        createOffer(
            {
                entityType: 'Rental',
                entityId: property.id,
                message: form.message,
                status: 'pending',
                senderId: user.id,
                receiverId: property.creator?.id,
                content: {
                    propertyId: property.id,
                    proposedRent: form.proposedRent,
                    message: form.message,
                },
            },
            {
                onSuccess: () => { setSent(true); setOfferOpen(false); reset(); },
            },
        );
    };

    if (isLoading) return (
        <div className="flex items-center justify-center py-32">
            <div className="w-10 h-10 border-4 rounded-full animate-spin"
                style={{ borderColor: 'var(--brand-dim)', borderTopColor: 'var(--brand)' }} />
        </div>
    );

    if (isNdaError) {
        return (
            <div className="max-w-3xl mx-auto py-20 text-center animate-fade-in">
                <div className="bg-white rounded-2xl shadow-sm border p-10 max-w-lg mx-auto">
                    <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock size={32} />
                    </div>
                    <h2 className="text-2xl font-bold mb-3 text-gray-800">Yêu cầu bảo mật thông tin</h2>
                    <p className="text-gray-500 mb-8 leading-relaxed">
                        Tin đăng này yêu cầu bạn phải ký Thỏa thuận bảo mật thông tin (NDA) trước khi xem chi tiết và trao đổi với người đăng.
                    </p>
                    <Button
                        onClick={() => setNdaOpen(true)}
                        className="w-full btn-brand py-3 text-base mb-3"
                    >
                        Ký NDA để xem chi tiết
                    </Button>
                    <Link to="/cho-thue">
                        <Button variant="ghost" className="w-full">
                            Quay lại danh sách
                        </Button>
                    </Link>
                </div>
                <NdaSignModal
                    isOpen={ndaOpen}
                    onClose={() => setNdaOpen(false)}
                    entityType="Rental"
                    entityId={id!}
                    onSuccess={() => {
                        setNdaOpen(false);
                        refetch();
                    }}
                />
            </div>
        );
    }

    if (!property) return (
        <div className="text-center py-24">
            <p className="text-5xl mb-4">🏢</p>
            <p className="font-bold text-xl mb-4" style={{ color: 'var(--text-1)' }}>Không tìm thấy mặt bằng</p>
            <Link to="/cho-thue">
                <button className="btn-outline-brand px-5 py-2 rounded-xl text-sm font-semibold">← Quay lại</button>
            </Link>
        </div>
    );

    const location = [property.streetAddress, property.ward?.name, property.district?.name, property.province?.name]
        .filter(Boolean).join(', ');

    const isOwner = user?.id && property.creator?.id && user.id === property.creator.id;

    return (
        <div className="max-w-5xl mx-auto animate-fade-in">
            {/* Breadcrumb */}
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-3)' }}>
                    <Link to="/cho-thue" className="flex items-center gap-1 hover:text-orange-500 transition-colors">
                        <ArrowLeft size={14} /> Cho thuê
                    </Link>
                    <ChevronRight size={12} />
                    <span className="truncate max-w-xs" style={{ color: 'var(--text-1)' }}>{property.title}</span>
                </div>
                {isOwner && (
                    <Link to={`/cho-thue/sua/${property.id}`}
                        className="px-4 py-2 rounded-xl text-sm font-semibold border transition-colors hover:bg-orange-50"
                        style={{ color: 'var(--brand)', borderColor: 'rgba(249,115,22,0.3)' }}>
                        Chỉnh sửa tin đăng
                    </Link>
                )}
            </div>

            <div className="grid lg:grid-cols-3 gap-6">

                {/* ══ LEFT ══ */}
                <div className="lg:col-span-2 space-y-5">

                    {/* Gallery */}
                    <MediaGallery
                      images={property.images ?? []}
                      statusBadgeText={property.isAvailable ? '✓ Còn trống' : '✗ Đã thuê'}
                      approvalStatus={property.approvalStatus}
                      fallbackIcon="🏢"
                    />

                    {/* Hero card */}
                    <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-3">
                            <span className="fnb-tag fnb-tag-brand text-xs capitalize">{property.businessType}</span>
                            <span className="fnb-tag text-xs capitalize">{property.propertyType}</span>
                            {property.furnishingStatus && (
                                <span className="fnb-tag text-xs">{property.furnishingStatus}</span>
                            )}
                            <span className={cn('fnb-tag text-xs font-semibold', property.isAvailable ? 'fnb-tag-green' : '')}>
                                {property.isAvailable ? '✓ Còn trống' : '✗ Đã thuê'}
                            </span>
                            {property.approvalStatus === 'approved' && (
                                <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium"
                                    style={{ background: '#dbeafe', borderColor: '#bfdbfe', color: '#1d4ed8' }}>
                                    <CheckCircle2 size={9} /> Đã xác thực
                                </span>
                            )}
                        </div>

                        <h1 className="font-display font-black text-2xl leading-tight mb-2" style={{ color: 'var(--text-1)' }}>
                            {property.title}
                        </h1>

                        <div className="flex items-center gap-1.5 text-sm mb-5" style={{ color: 'var(--text-3)' }}>
                            <MapPin size={13} style={{ color: 'var(--brand)' }} />
                            <span>{location}</span>
                        </div>

                        {/* Key stats */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <StatBox icon={<DollarSign size={13} />} label="Giá thuê/tháng" accent
                                value={fmtMoney(property.monthlyRent, property.currencyUnit)} />
                            <StatBox icon={<Layers size={13} />} label="Diện tích"
                                value={`${parseFloat(property.area).toFixed(0)} ${property.areaUnit}`} />
                            <StatBox icon={<Building2 size={13} />} label="Tầng"
                                value={property.floorLevel ? `Tầng ${property.floorLevel}` : '—'} />
                            <StatBox icon={<Users size={13} />} label="Sức chứa"
                                value={property.seatingCapacity ? `${property.seatingCapacity} chỗ` : '—'} />
                        </div>
                    </div>

                    {/* Description */}
                    {property.description && (
                        <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
                            <h2 className="font-bold text-base mb-3 flex items-center gap-2" style={{ color: 'var(--text-1)' }}>
                                <FileText size={16} style={{ color: 'var(--brand)' }} /> Mô tả mặt bằng
                            </h2>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--text-2)' }}>
                                {property.description}
                            </p>
                        </div>
                    )}

                    {/* Diện tích chi tiết */}
                    <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
                        <h2 className="font-bold text-base mb-4 flex items-center gap-2" style={{ color: 'var(--text-1)' }}>
                            <Layers size={16} style={{ color: 'var(--brand)' }} /> Thông tin diện tích
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {[
                                { label: 'Tổng diện tích', value: property.area, unit: property.areaUnit },
                                { label: 'Diện tích bếp', value: property.kitchenArea, unit: property.areaUnit },
                                { label: 'Phòng ăn', value: property.diningArea, unit: property.areaUnit },
                                { label: 'Ngoài trời', value: property.outdoorSeatingArea, unit: property.areaUnit },
                                { label: 'Kho', value: property.storageArea, unit: property.areaUnit },
                                { label: 'Giá/m²', value: property.pricePerArea, unit: `${property.currencyUnit}/m²` },
                            ].filter(r => r.value).map(r => (
                                <div key={r.label} className="rounded-xl p-3 border"
                                    style={{ background: 'var(--surface-1)', borderColor: 'var(--border-c)' }}>
                                    <p className="text-[10px] font-medium mb-0.5" style={{ color: 'var(--text-3)' }}>{r.label}</p>
                                    <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
                                        {formatNumber(parseFloat(r.value!))} {r.unit}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Sức chứa */}
                        {(property.seatingCapacity || property.maxOccupancy) && (
                            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-3" style={{ borderColor: 'var(--border-c)' }}>
                                {property.seatingCapacity && (
                                    <div>
                                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Sức chứa (chỗ ngồi)</p>
                                        <p className="font-bold text-sm mt-0.5" style={{ color: 'var(--text-1)' }}>
                                            {property.seatingCapacity} chỗ
                                        </p>
                                    </div>
                                )}
                                {property.maxOccupancy && (
                                    <div>
                                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Sức chứa tối đa</p>
                                        <p className="font-bold text-sm mt-0.5" style={{ color: 'var(--text-1)' }}>
                                            {property.maxOccupancy} người
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Tiện nghi bếp */}
                    <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
                        <h2 className="font-bold text-base mb-4 flex items-center gap-2" style={{ color: 'var(--text-1)' }}>
                            <Utensils size={16} style={{ color: 'var(--brand)' }} /> Tiện nghi & Thiết bị
                        </h2>
                        <div>
                            <FeatureRow icon={<FlameKindling size={14} />} label="Đường gas"
                                value={property.gasConnection} />
                            <FeatureRow icon={<Zap size={14} />} label="Hệ thống hút khói (Hood)"
                                value={property.hasHoodSystem} detail={property.hoodSystemType} />
                            <FeatureRow icon={<Droplets size={14} />} label="Bẫy mỡ (Grease Trap)"
                                value={property.greaseTrap} detail={property.greaseTrapeSize} />
                            <FeatureRow icon={<Shield size={14} />} label="Giấy phép rượu"
                                value={property.hasLiquorLicense} detail={property.liquorLicenseType} />
                            <FeatureRow icon={<Package size={14} />} label="Khu vực kho"
                                value={property.hasStorage} />
                            <FeatureRow icon={<Car size={14} />} label="Bãi đậu xe"
                                value={property.hasParking}
                                detail={property.parkingSpaces ? `${property.parkingSpaces} chỗ` : null} />
                            <FeatureRow icon={<Users size={14} />} label="Toilet nhân viên"
                                value={property.hasEmployeeBathroom} />
                            <FeatureRow icon={<Users size={14} />} label="Toilet khách hàng"
                                value={property.hasCustomerBathroom} />
                            <FeatureRow icon={<Building2 size={14} />} label="Cửa giao hàng riêng"
                                value={property.hasDeliveryEntrance} />
                            <FeatureRow icon={<MapPin size={14} />} label="Khu ngoài trời"
                                value={property.hasOutdoorSeating} />
                        </div>

                        {/* Equipment list */}
                        {property.kitchenEquipmentIncluded && (
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-c)' }}>
                                <p className="text-xs font-semibold uppercase tracking-wide mb-1.5"
                                    style={{ color: 'var(--text-3)' }}>Thiết bị bếp bao gồm</p>
                                <p className="text-sm" style={{ color: 'var(--text-2)' }}>
                                    {property.kitchenEquipmentIncluded}
                                </p>
                            </div>
                        )}

                        {/* Capacity specs */}
                        {(property.waterCapacity || property.electricalCapacity) && (
                            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-3" style={{ borderColor: 'var(--border-c)' }}>
                                {property.waterCapacity && (
                                    <div>
                                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Sức chứa nước</p>
                                        <p className="font-semibold text-sm" style={{ color: 'var(--text-1)' }}>{property.waterCapacity}</p>
                                    </div>
                                )}
                                {property.electricalCapacity && (
                                    <div>
                                        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Công suất điện</p>
                                        <p className="font-semibold text-sm" style={{ color: 'var(--text-1)' }}>{property.electricalCapacity}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Health rating */}
                        {property.healthDepartmentRating && (
                            <div className="mt-4 pt-4 border-t flex items-center gap-2" style={{ borderColor: 'var(--border-c)' }}>
                                <Star size={14} style={{ color: '#f59e0b' }} />
                                <span className="text-sm" style={{ color: 'var(--text-2)' }}>
                                    Đánh giá VSATTP: <strong>Hạng {property.healthDepartmentRating}</strong>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Floor plan */}
                    {property.floorPlanUrl && (
                        <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
                            <h2 className="font-bold text-base mb-3 flex items-center gap-2" style={{ color: 'var(--text-1)' }}>
                                <FileText size={16} style={{ color: 'var(--brand)' }} /> Sơ đồ mặt bằng
                            </h2>
                            <img src={property.floorPlanUrl} alt="Floor plan"
                                className="w-full rounded-xl border object-contain max-h-96"
                                style={{ borderColor: 'var(--border-c)' }} />
                        </div>
                    )}
                </div>

                {/* ══ RIGHT: Sidebar ══ */}
                <div className="space-y-4">

                    {/* Price + CTA card */}
                    <div className="rounded-2xl border p-5 sticky top-24"
                        style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>

                        {/* Price highlight */}
                        <div className="rounded-xl p-4 mb-4 text-center"
                            style={{ background: 'var(--brand-pale)', border: '1px solid rgba(249,115,22,0.2)' }}>
                            <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--text-3)' }}>Giá thuê / tháng</p>
                            <p className="font-display font-black text-2xl" style={{ color: 'var(--brand)' }}>
                                {fmtMoney(property.monthlyRent, '')}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                                {property.currencyUnit}/tháng
                                {property.pricePerArea && ` · ${formatNumber(parseFloat(property.pricePerArea))} ${property.currencyUnit}/m²`}
                            </p>
                        </div>

                        {/* Deposit + Lease */}
                        {(property.securityDeposit || property.leaseTerm) && (
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                {property.securityDeposit && (
                                    <div className="rounded-lg p-2.5 text-center" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-c)' }}>
                                        <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>Tiền cọc</p>
                                        <p className="text-xs font-bold mt-0.5" style={{ color: 'var(--text-1)' }}>
                                            {fmtMoney(property.securityDeposit, '')}
                                        </p>
                                    </div>
                                )}
                                {property.leaseTerm && (
                                    <div className="rounded-lg p-2.5 text-center" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-c)' }}>
                                        <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>Thời hạn thuê</p>
                                        <p className="text-xs font-bold mt-0.5" style={{ color: 'var(--text-1)' }}>
                                            {property.leaseTerm} tháng
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Success */}
                        {sent && !offerOpen && (
                            <div className="mb-3 p-3 rounded-xl border flex items-center gap-2 text-sm"
                                style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' }}>
                                <CheckCircle2 size={15} /> Đã gửi yêu cầu thuê thành công!
                            </div>
                        )}

                        {/* Offer actions */}
                        <div className="space-y-2">
                            {isOwner ? null : isAuthenticated ? (
                                needsToSignNda ? (
                                    <button onClick={() => setNdaOpen(true)}
                                        className="btn-brand w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold mt-4">
                                        <Lock size={15} /> Ký NDA để Gửi yêu cầu
                                    </button>
                                ) : (
                                    <button onClick={() => setOfferOpen(true)}
                                        disabled={isOffered || sent}
                                        className={cn(
                                            "w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold mt-4 transition-all",
                                            (isOffered || sent)
                                                ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                                                : "btn-brand"
                                        )}>
                                        <Send size={15} /> {isOffered || sent ? 'Đã gửi yêu cầu' : 'Yêu cầu thuê'}
                                    </button>
                                )
                            ) : (
                                <Link to="/login"
                                    className="btn-brand w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold mt-4">
                                    Đăng nhập để liên hệ
                                </Link>
                            )}

                            {/* Save listing */}
                            {(!user || user.id !== property.creator?.id) && (
                                <button
                                    onClick={() => toggleSave(property.id, "Rental")}
                                    className={cn(
                                        "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 border transition-all mt-2",
                                        saved
                                            ? "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"
                                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                                    )}
                                >
                                    <Heart size={14} className={cn(saved && "fill-orange-500 text-orange-500")} />
                                    {saved ? "Đã lưu tin" : "Lưu tin"}
                                </button>
                            )}

                            {/* Contact direct */}
                            {property.contactPhone && (
                                <a href={`tel:${property.contactPhone}`}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-colors hover:border-orange-400 hover:text-orange-500"
                                    style={{ borderColor: 'var(--border-c)', color: 'var(--text-2)' }}>
                                    <Phone size={14} /> {property.contactPhone}
                                </a>
                            )}
                            {property.contactEmail && (
                                <a href={`mailto:${property.contactEmail}`}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-colors hover:border-orange-400 hover:text-orange-500"
                                    style={{ borderColor: 'var(--border-c)', color: 'var(--text-2)' }}>
                                    <Mail size={14} /> {property.contactEmail}
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Meta info */}
                    <div className="rounded-2xl border p-5" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
                        <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--text-1)' }}>Thông tin chi tiết</h3>
                        <div className="space-y-0">
                            {[
                                { icon: <Building2 size={13} />, label: 'Loại BĐS', value: property.propertyType },
                                { icon: <Utensils size={13} />, label: 'Loại hình KD', value: property.businessType },
                                { icon: <Layers size={13} />, label: 'Tầng', value: property.floorLevel ? `Tầng ${property.floorLevel}` : null },
                                { icon: <MapPin size={13} />, label: 'Địa chỉ', value: property.streetAddress },
                                { icon: <MapPin size={13} />, label: 'Quận/Huyện', value: property.district?.name },
                                { icon: <MapPin size={13} />, label: 'Tỉnh/Thành', value: property.province?.name },
                                { icon: <Calendar size={13} />, label: 'Có thể thuê từ', value: property.availableFrom ? new Date(property.availableFrom).toLocaleDateString('vi-VN') : null },
                                { icon: <Clock size={13} />, label: 'Thời hạn thuê', value: property.leaseTerm ? `${property.leaseTerm} tháng` : null },
                            ].filter(r => r.value).map(({ icon, label, value }) => (
                                <div key={label} className="flex items-start justify-between gap-2 py-2 border-b last:border-0"
                                    style={{ borderColor: 'var(--border-c)' }}>
                                    <div className="flex items-center gap-1.5 shrink-0" style={{ color: 'var(--text-3)' }}>
                                        {icon} <span className="text-xs">{label}</span>
                                    </div>
                                    <span className="text-xs font-semibold text-right capitalize"
                                        style={{ color: 'var(--text-1)' }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Additional costs */}
                    {(property.nnnCosts || property.camFees || property.utilityEstimate) && (
                        <div className="rounded-2xl border p-5" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
                            <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--text-1)' }}>Chi phí phát sinh</h3>
                            {[
                                { label: 'NNN Costs', value: property.nnnCosts },
                                { label: 'CAM Fees', value: property.camFees },
                                { label: 'Ước tính điện nước', value: property.utilityEstimate },
                            ].filter(r => r.value).map(r => (
                                <div key={r.label} className="flex justify-between py-1.5 border-b last:border-0"
                                    style={{ borderColor: 'var(--border-c)' }}>
                                    <span className="text-xs" style={{ color: 'var(--text-3)' }}>{r.label}</span>
                                    <span className="text-xs font-semibold" style={{ color: 'var(--text-1)' }}>
                                        {fmtMoney(r.value, property.currencyUnit)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ══ OFFER MODAL ══ */}
            <OfferModal
                isOpen={offerOpen}
                onClose={() => { setOfferOpen(false); reset(); }}
                title="Yêu cầu thuê"
                onSubmit={handleSubmit(onSubmit)}
                isPending={isPending}
                submitText="Gửi yêu cầu"
            >
                <NumberInput
                    label="Giá thuê đề xuất (VNĐ)"
                    placeholder="45,000,000"
                    hint="Để trống nếu đồng ý giá niêm yết"
                    error={errors.proposedRent?.message}
                    {...register('proposedRent')}
                />
                <Textarea
                    label="Lời nhắn *"
                    placeholder="Giới thiệu bản thân, mục đích thuê mặt bằng, thời gian mong muốn..."
                    rows={4}
                    error={errors.message?.message}
                    {...register('message')}
                />
            </OfferModal>

            {/* ══ NDA MODAL ══ */}
            <NdaSignModal
                isOpen={ndaOpen}
                onClose={() => setNdaOpen(false)}
                entityType="Rental"
                entityId={property.id}
                description="Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để trao đổi với người đăng."
                onSuccess={() => {
                    setNdaOpen(false);
                    setOfferOpen(true);
                }}
            />
        </div>
    );
}
